/**
 * Lead and Commercial Group Schemas
 *
 * This module contains canonical schemas for leads and commercial groups.
 * These are the single source of truth - other files should import from here.
 */

import { z } from 'zod';
import { AcquisitionChannelTypeEnum } from '@components/molecules/add-commercial-group';
import { DocTypeEnum } from './enums-definitions';
import { validateCommercialGroup } from './validators';

// ============================================
// LEAD SCHEMAS
// ============================================

/**
 * LeadPayloadSchema - Schema for lead information used in simulations
 * Used across the application for lead data in simulation payloads
 */
export const LeadPayloadSchema = z.object({
  leadId: z.string().nullish(),
  name: z.string().nullish(),
  docNumber: z.string().nullish(),
  docType: DocTypeEnum.default('CNPJ').nullish(),
  clientId: z.string().nullish(),
  companyId: z.string().nullish(),
  unitId: z.string().nullish(),
});

// ============================================
// COMMERCIAL GROUP SCHEMAS
// ============================================

const CommercialGroupTypeEnumSchema = z
  .enum(['MANAGEMENT_RETAIL', 'MANAGEMENT_WHOLESALE', 'RETAIL', 'LEAD', 'RETAIL_TRANSFER_CONTRACTS'])
  .nullish();

/**
 * CommercialGroupPayloadSchema - Schema for commercial group creation/update
 * TYPE REFERENCE: https://studio.apollographql.com/graph/clarke-gateway-8ehyip/variant/beta/schema/visualization?focused=TYPE-CommercialGroup&showDetails=true&subgraphs=customer
 * This is the canonical schema for commercial group data - extend from this for specialized use cases
 */
export const CommercialGroupPayloadSchema = z
  .object({
    name: z.string().nullish(),
    legalName: z.string().nullish(),
    docType: z.enum(['CNPJ', 'CPF'], {
      errorMap: () => ({ message: 'Campo é obrigatório' }),
    }),
    docNumber: z.string().nullish(),
    origination: z
      .enum(Object.keys(AcquisitionChannelTypeEnum) as [keyof typeof AcquisitionChannelTypeEnum], {
        errorMap: () => ({ message: 'Campo é obrigatório' }),
      })
      .nullish(),
    type: CommercialGroupTypeEnumSchema.nullish(),
    notes: z.string().nullish(),
    volume: z.number().nullish(),
  })
  .superRefine((data, ctx) => {
    validateCommercialGroup(data, ctx);
  });

/**
 * CommercialGroupSchema - Complete commercial group schema with ID
 * Note: Cannot merge/extend schemas with superRefine - temporary duplication
 * TODO: Find better solution to avoid duplication
 */
export const CommercialGroupSchema = z.object({
  id: z.string(),
  groupId: z.string().nullish(),
  name: z.string().nullish(),
  legalName: z.string().nullish(),
  docType: z
    .enum(['CNPJ', 'CPF'], {
      errorMap: () => ({ message: 'Campo é obrigatório' }),
    })
    .nullish(),
  docNumber: z.string().nullish(),
  origination: z
    .enum(Object.keys(AcquisitionChannelTypeEnum) as [keyof typeof AcquisitionChannelTypeEnum], {
      errorMap: () => ({ message: 'Campo é obrigatório' }),
    })
    .nullish(),
  type: CommercialGroupTypeEnumSchema.nullish(),
  notes: z.string().nullish(),
  volume: z.number().nullish(),
  createdAt: z.union([z.string().nullish(), z.date().nullish()]).nullish(),
});

// ============================================
// SELLER SCHEMA
// ============================================

export const SellerSchema = z.object({
  id: z.string().nullish(),
  name: z.string().nullish(),
  pictureUrl: z.string().url().nullish(),
});
